import React from 'react'
import KEYCODE from 'keycode'

export default AutoSuggest => {
  class AutoSuggestLogicContainer extends React.Component {
    constructor(props) {
      super(props)
      this.state = {
        inputValue: props.inputValue,
        isOpen: false,
        focusedIndex: -1
      }

      this.handleInputFocus = this.handleInputFocus.bind(this)
      this.handleInputChange = this.handleInputChange.bind(this)
      this.handleKeyDown = this.handleKeyDown.bind(this)
      this.handleKeyUp = this.handleKeyUp.bind(this)
      this.setWrapperRef = this.setWrapperRef.bind(this)
      this.handleDocumentClick = this.handleDocumentClick.bind(this)
      this.handleMenuMouseEnter = this.handleMenuMouseEnter.bind(this)
      this.handleMenuMouseLeave = this.handleMenuMouseLeave.bind(this)
    }

    componentDidMount() {
      document.addEventListener('click', this.handleDocumentClick, true)
      document.addEventListener('touchend', this.handleDocumentClick, true)
    }

    componentWillUnmount() {
      document.removeEventListener('click', this.handleDocumentClick, true)
      document.removeEventListener('touchend', this.handleDocumentClick, true)
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
      if (nextProps.inputValue !== this.props.inputValue) {
        this.setState({
          inputValue: nextProps.inputValue
        })
      }
    }

    setWrapperRef(ref) {
      if (ref !== null) {
        this.wrapper = ref
      }
    }

    setTextInputRef(ref) {
      if (ref !== null) {
        this.textInput = ref
      }
    }

    setMenuRef(ref) {
      if (ref !== null) {
        this.menu = ref
      }
    }

    handleDocumentClick(event) {
      if (!this.wrapper.contains(event.target)) {
        this.setState({ isOpen: false })
      }
    }

    handleButtonFocus() {
      this.setState({ isOpen: false })
    }

    handleInputChange(event) {
      if (this.props.onInputChange) {
        this.props.onInputChange(event.target.value)
      }

      // if auto suggest menu is not open, we should open it on input change
      if (!this.state.isOpen) {
        this.setState({
          isOpen: true
        })
      }
      this.setState({ inputValue: event.target.value })
    }

    handleInputFocus(event) {
      this.setState({
        isOpen: true
      })

      if (this.props.onInputFocus) {
        this.props.onInputFocus(event.target.value)
      }
    }

    onMenuClick(option, event) {
      event.preventDefault()

      if (this.props.onSelectOption) {
        this.props.onSelectOption(option, event)
      }

      this.setState({
        isOpen: false
      })
    }

    handleKeyDown(event) {
      const keyCode = event.which

      switch (keyCode) {
        // Block up/down arrow from moving the cursor in the search input
        case KEYCODE('up'):
        case KEYCODE('down'):
          event.preventDefault()
          break

        // 1. Because Enter triggers form submission before we get the chance to run
        //    `_updateAnalyticsParamInput`, we disable the submission on keydown and
        //    manually trigger it on keyup. Ideally we'd have the form and the input
        //    as custom React components and then this wouldn't be necessary.
        // 2. Block Esc from emptying the search input
        case KEYCODE('enter'):
        case KEYCODE('esc'):
          event.preventDefault()
          break
      }
    }

    handleKeyUp(event) {
      const { focusedIndex, inputValue } = this.state
      const { suggestions } = this.props

      const keyCode = event.which

      switch (keyCode) {
        case KEYCODE('enter'):
          if (this.props.onSelectOption) {
            // when the user hit `enter` without selecting any option, we simply pass the inputValue to onSelection option
            this.props.onSelectOption(
              focusedIndex === -1 ? inputValue : suggestions[focusedIndex],
              event
            )
          }

          this.setState({
            isOpen: false
          })
          break

        // Esc hides autosuggest and deselects the current suggestion
        case KEYCODE('esc'):
          this.setState({
            isOpen: false,
            focusedIndex: -1
          })
          break

        case KEYCODE('up'):
          this.setFocusedIndex('prev')
          break

        case KEYCODE('down'):
          this.setFocusedIndex('next')
          break

        default:
          this.setState({
            focusedIndex: -1
          })
          event.preventDefault()
      }
    }

    setFocusedIndex(dir) {
      const { focusedIndex, inputValue } = this.state
      const { suggestions } = this.props
      const optionLength = suggestions.length

      const isNoneSelected = focusedIndex === -1
      const isFirstSelected = focusedIndex === 0
      const isLastSelected = focusedIndex === optionLength - 1

      if (dir === 'prev') {
        if (isFirstSelected || isNoneSelected) {
          this.setState({
            focusedIndex: optionLength - 1,
            inputValue: suggestions[optionLength - 1]
              ? suggestions[optionLength - 1].text
              : inputValue
          })
        } else {
          this.setState({
            focusedIndex: focusedIndex - 1,
            inputValue: suggestions[focusedIndex - 1]
              ? suggestions[focusedIndex - 1].text
              : inputValue
          })
        }
      } else if (dir === 'next') {
        if (isLastSelected || isNoneSelected) {
          this.setState({
            focusedIndex: 0,
            inputValue: suggestions[0] ? suggestions[0].text : inputValue
          })
        } else {
          this.setState({
            focusedIndex: focusedIndex + 1,
            inputValue: suggestions[focusedIndex + 1]
              ? suggestions[focusedIndex + 1].text
              : inputValue
          })
        }
      }
    }

    handleMenuMouseEnter(index) {
      this.setState({
        focusedIndex: index
      })
    }

    handleMenuMouseLeave() {
      this.setState({
        focusedIndex: -1
      })
    }

    onSearchDismiss() {
      this.setState({
        inputValue: ''
      })
    }

    render() {
      const { focusedIndex, isOpen, inputValue } = this.state
      const props = {
        ...this.props,
        focusedIndex,
        inputValue,
        isOpen,
        wrapperRef: this.wrapper,
        textInputRef: this.textInput,
        setWrapperRef: this.setWrapperRef.bind(this),
        setMenuRef: this.setMenuRef.bind(this),
        setTextInputRef: this.setTextInputRef.bind(this),
        onMenuClick: this.onMenuClick.bind(this),
        handleMenuMouseEnter: this.handleMenuMouseEnter.bind(this),
        handleMenuMouseLeave: this.handleMenuMouseLeave.bind(this),
        handleInputChange: this.handleInputChange.bind(this),
        handleInputFocus: this.handleInputFocus.bind(this),
        handleKeyDown: this.handleKeyDown.bind(this),
        handleKeyUp: this.handleKeyUp.bind(this),
        onSearchDismiss: this.onSearchDismiss.bind(this),
        handleButtonFocus: this.handleButtonFocus.bind(this)
      }

      return <AutoSuggest {...props} />
    }
  }

  return AutoSuggestLogicContainer
}
